import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { X } from "@phosphor-icons/react";
import { defaultProject, projectSchema } from "@reactive-resume/schema";
import { Info} from "@phosphor-icons/react";
import {
  Badge,
  BadgeInput,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RichInput,
  Tooltip,
} from "@reactive-resume/ui";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { AiActions } from "@/client/components/ai-actions";
import { useResumeStore } from "@/client/stores/resume";

import { MonthYearDatePicker } from "../sections/shared/month-year-date-picker";
import { SectionDialog } from "../sections/shared/section-dialog";
import { URLInput } from "../sections/shared/url-input";
import { MastersUnionListEditor } from "../sections/masters-union-summary";

const formSchema = projectSchema;

type FormValues = z.infer<typeof formSchema>;

export const ProjectsDialog = () => {
  const form = useForm<FormValues>({
    defaultValues: defaultProject,
    resolver: zodResolver(formSchema),
  });

  const [pendingKeyword, setPendingKeyword] = useState("");
  const template = useResumeStore((state) => state.resume.data?.metadata?.template);
  const isMastersUnion = template === "mastersunion";

  // Auto-fill name field for mastersunion template
  useEffect(() => {
    if (isMastersUnion) {
      const currentName = form.getValues("name");
      if (!currentName || currentName === "") {
        form.setValue("name", "Project", { shouldValidate: true });
      }
    }
  }, [isMastersUnion, form]);

  return (
    <SectionDialog<FormValues>
      id="projects"
      form={form}
      defaultValues={defaultProject}
      pendingKeyword={pendingKeyword}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          name="name"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Name`}</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Project Name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="description"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Description`}</FormLabel>
              <FormControl>
                <Input {...field} maxLength={60} placeholder="Project Description" />
              </FormControl>
              <p className="text-xs text-muted-foreground">{`Character limit ${(field.value ?? "").length}/60`}</p>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="date"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t`Date or Date Range`}</FormLabel>
              <FormControl>
                <MonthYearDatePicker value={field.value} onChange={field.onChange} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {!isMastersUnion && (
          <FormField
            name="url"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t`Website`}</FormLabel>
                <FormControl>
                  <URLInput {...field} placeholder="https://rxresu.me" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          name="location"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Location`}</FormLabel>
              <FormControl>
                <Input {...field} placeholder="City, Country" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="summary"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              {/* <FormLabel>{t`Summary`}</FormLabel> */}
              <FormLabel className="flex items-center gap-2">
                <span>{t`Summary`}</span>
                {isMastersUnion && (
                  <Tooltip content="Click on “Add More” to add more bullet points">
                    <span className="inline-flex cursor-help text-muted-foreground">
                      <Info size={14} />
                    </span>
                  </Tooltip>
                )}
              </FormLabel>
              <FormControl>
                {isMastersUnion ? (
                  <MastersUnionListEditor
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                ) : (
                  <RichInput
                    {...field}
                    content={field.value}
                    onChange={(value) => field.onChange(value)}
                    footer={(editor) => (
                      <AiActions
                        value={editor.getText()}
                        content={editor.getHTML()}
                        onChange={(value) => {
                          editor.commands.setContent(value);
                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {!isMastersUnion && (
          <FormField
            name="keywords"
            control={form.control}
            render={({ field }) => (
              <div className="space-y-3 sm:col-span-2">
                <FormItem>
                  <FormLabel>{t`Keywords`}</FormLabel>
                  <FormControl>
                    <BadgeInput {...field} setPendingKeyword={setPendingKeyword} />
                  </FormControl>
                  <FormDescription>
                    {t`You can add multiple keywords by separating them with a comma or pressing enter.`}
                  </FormDescription>
                  <FormMessage />
                </FormItem>

                <div className="flex flex-wrap items-center gap-x-2 gap-y-3">
                  <AnimatePresence>
                    {field.value.map((item, index) => (
                      <motion.div
                        layout
                        key={item}
                        initial={{ opacity: 0, y: -50 }}
                        animate={{ opacity: 1, y: 0, transition: { delay: index * 0.1 } }}
                        exit={{ opacity: 0, x: -50 }}
                      >
                        <Badge
                          className="cursor-pointer"
                          onClick={() => {
                            field.onChange(field.value.filter((v) => item !== v));
                          }}
                        >
                          <span className="mr-1">{item}</span>
                          <X size={12} weight="bold" />
                        </Badge>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            )}
          />
        )}
      </div>
    </SectionDialog>
  );
};
